import {useEffect} from 'react';
import {router} from '@inertiajs/react';
import toast from 'react-hot-toast';
import {AnalyticsEvent, sendEvent} from '@/services/analytics';
import {isSettled, waitForPaymentSettlement} from '@/services/paymentStatusService';
import {PaymentStatus} from '@/types/enums';

const showSettledToast = (status: PaymentStatus, id?: string) => {
    switch (status) {
        case PaymentStatus.COMPLETED:
            toast.success('Payment successful', {id});
            break;
        case PaymentStatus.FAILED:
            toast.error('Payment failed', {id});
            break;
        case PaymentStatus.CANCELLED:
            break;
        case PaymentStatus.REFUNDED:
            toast.error('Payment refunded', {id});
            break;
    }
};

export default function usePaymentFlash(sessionFlash?: any) {
    useEffect(() => {
        if (!sessionFlash?.data?.payment) {
            return;
        }

        const payment = sessionFlash.data.payment;
        const status = payment.status as PaymentStatus;

        if (status === PaymentStatus.COMPLETED) {
            sendEvent(AnalyticsEvent.PURCHASE, {
                value: payment.amount / 100,
                currency: 'USD',
                transaction_id: payment.id,
            });
        }

        if (payment.async && window.opener) {
            window.close();
            return;
        }

        if (status !== PaymentStatus.PENDING || !payment.id) {
            showSettledToast(status);
            return;
        }

        const controller = new AbortController();
        const toastId = toast.loading('Confirming payment...');

        waitForPaymentSettlement(payment.id, {signal: controller.signal}).then(settled => {
            if (controller.signal.aborted) {
                toast.dismiss(toastId);
                return;
            }

            if (!isSettled(settled)) {
                toast('Payment is pending. It will be credited once confirmed.', {icon: '⏳', id: toastId});
                return;
            }

            showSettledToast(settled, toastId);

            if (settled === PaymentStatus.COMPLETED) {
                router.reload();
            }
        });

        return () => controller.abort();
    }, [sessionFlash]);
}
